import { get } from 'lodash';
import { type FC } from 'react';

import AccountFormWrapper from '@zen/Accounts/components/forms/AccountFormWrapper';
import LocationUsageRadioGroup from '@zen/Accounts/components/forms/LocationUsageRadioGroup';
import { useEntitledAccountOptions } from '@zen/Accounts/hooks/useEntitledAccountOptions';
import { FormInput, FormSelect } from '@zen/Components';
import type { FormInstance } from '@zen/Components/types';
import { OrganizationalRoleEnum, UsageContext } from '@zen/graphql/types.generated';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import { useUpdateAccountLocationDetailsMutation } from '../../graphql';
import { prepareValues } from './helpers';
import { locationDetailsValidation } from './locationDetails.validation';
import type { LocationDetailsFormValues } from './types';

type Props = {
  accountId: string;
  initialValues: LocationDetailsFormValues;
  locationAccountId: string;
  locationId: string;
  onCancel?: () => void;
  onSuccess?: () => void;
};

const UpdateLocationDetails: FC<Props> = ({ accountId, initialValues, locationAccountId, locationId, onCancel, onSuccess }) => {
  const [updateLocation] = useUpdateAccountLocationDetailsMutation({ refetchQueries: ['getAccountLocation'] });
  const { addError, addSuccess } = useNotification();

  const { options, loading, onSearch, getOrganizationalRole } = useEntitledAccountOptions({
    accountId,
    selectedAccountId: locationAccountId
  });

  const handleUpdateLocationDetails = async (formValues: LocationDetailsFormValues): Promise<IOkOrErrorResult> => {
    return performFormMutation({
      mutationFn: () =>
        updateLocation({
          variables: {
            input: prepareValues({ formValues, locationId, accountId })
          }
        }),
      onError: (errors) => {
        addError(get(errors, '[0].message', 'There was a problem updating the location details.'));
      },
      onSuccess: () => addSuccess('Location details updated.')
    });
  };

  return (
    <AccountFormWrapper<LocationDetailsFormValues>
      initialValues={initialValues}
      onCancel={onCancel}
      onSubmit={handleUpdateLocationDetails}
      onSuccess={onSuccess}
      validationSchema={locationDetailsValidation}
    >
      {({ values, setFieldValue }: FormInstance<LocationDetailsFormValues>) => {
        const organizationalRole = getOrganizationalRole(values.locationAccountId);
        const isConsignmentParty = organizationalRole ? organizationalRole === OrganizationalRoleEnum.CONSIGNMENT_PARTY : true;
        const isUsageContextSetToBoth = values.usage === UsageContext.BOTH;

        if (!isConsignmentParty && !isUsageContextSetToBoth) {
          setFieldValue('usage', UsageContext.BOTH);
        }

        return (
          <>
            <FormSelect
              className="mb-4"
              isLoading={loading}
              isSearchable={true}
              label="This location is owned by:"
              name="locationAccountId"
              onInputChange={onSearch}
              options={options}
              renderMenuInPortal={true}
            />

            <FormInput label="Location name" name="name" />
            {isConsignmentParty && <LocationUsageRadioGroup name="usage" noMargin={true} />}
          </>
        );
      }}
    </AccountFormWrapper>
  );
};

export default UpdateLocationDetails;
export type { Props as UpdateLocationDetailsProps };
